import type { FormType } from "../../types";
import type { Dispatch, FC, SetStateAction } from "react";
import type { SelectChangeEvent, Theme } from "@mui/material";

import { Box, Stack, Typography } from "@mui/material";
import CustomButton from "../../CustomButton";
import CustomTextField from "../../CustomTextField";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

import { useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@mui/styles";
import { menuItems } from "../../../constants/forms";
import { getPairingFormValuesToHide } from "../../../utils/forms/getPairingFormValuesToHide";
import { getActiveWebUrl } from "../../../services/selectors/web-selectors";
import { UrlPairingFormItem } from "../../UrlPairingFormItem";

interface NotificationPairingTabProps {
  forms: FormType[];
  forOrders: boolean;
  onDeletePairingForm: (
    index: number,
    setForms: Dispatch<SetStateAction<FormType[]>>,
  ) => void;
  onAddPairingFormIncludeChip: (
    index: number,
    value: string[],
    setForms: Dispatch<SetStateAction<FormType[]>>,
  ) => void;
  onAddPairingFormExcludeChip: (
    index: number,
    value: string[],
    setForms: Dispatch<SetStateAction<FormType[]>>,
  ) => void;
  handlePairingFormControlChange: (
    event: SelectChangeEvent,
    index: number,
    setForms: Dispatch<SetStateAction<FormType[]>>,
  ) => void;
  addEmptyPairingForm: (
    forms: FormType[],
    setForms: Dispatch<SetStateAction<FormType[]>>,
  ) => void;
  setForms: Dispatch<SetStateAction<FormType[]>>;
  type?: "actualVisitors" | "summaryModal";
  selectedVariant?: string;
}

export const NotificationPairingTab: FC<NotificationPairingTabProps> = ({
  forms,
  forOrders,
  type,
  selectedVariant,
  onDeletePairingForm,
  onAddPairingFormIncludeChip,
  onAddPairingFormExcludeChip,
  handlePairingFormControlChange,
  addEmptyPairingForm,
  setForms,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const activeWebUrl = useSelector(getActiveWebUrl);

  const dropdownItems = menuItems.map(item => ({
    value: item.value,
    title:
      item.value === "beginsWith"
        ? t("campaign.settings.url.startsWith")
        : item.value === "contains"
        ? t("campaign.settings.url.includes")
        : t("campaign.settings.url.equals") ?? "",
  }));

  const hiddenValues = getPairingFormValuesToHide(forms);

  const renderForms = () => (
    <Box sx={{ width: 460 }}>
      {forms.map((form, index) => (
        <UrlPairingFormItem
          key={index}
          index={index}
          form={form}
          dropdownItems={dropdownItems}
          hiddenValues={hiddenValues}
          handlePairingFormControlChange={handlePairingFormControlChange}
          onDeletePairingForm={onDeletePairingForm}
          onAddPairingFormIncludeChip={onAddPairingFormIncludeChip}
          onAddPairingFormExcludeChip={onAddPairingFormExcludeChip}
          setForms={setForms}
          t={t}
          classes={classes}
        />
      ))}
      {forms.length < 3 && (
        <CustomButton
          size='small'
          style={{ width: 180 }}
          type='secondary'
          title={
            type
              ? t("campaign.settings.addCondition")
              : t("campaign.notifications.settings.pairing.addCondition")
          }
          onClick={() => addEmptyPairingForm(forms, setForms)}
        />
      )}
    </Box>
  );

  if (!type) {
    return (
      <Box
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: 650,
          boxSizing: "border-box",
          padding: "16px 8px 16px 0",
          overflow: "auto",
        }}
      >
        <Stack direction='row' alignItems='center' pb={1}>
          <Typography className={classes.secondTitle}>
            {t("campaign.notifications.settings.pairing.collectData")}
          </Typography>
          <HelpOutlineOutlinedIcon
            titleAccess={t("help") ?? ""}
            sx={{
              color: theme.palette.natural.t2,
              marginLeft: 1,
              cursor: "pointer",
            }}
            onClick={() =>
              window.open(
                forOrders
                  ? process.env.REACT_APP_LAST_ORDER_HELP_URL
                  : process.env.REACT_APP_LAST_REGISTRATIONS_HELP_URL,
              )
            }
          />
        </Stack>
        {renderForms()}
      </Box>
    );
  }

  if (type === "summaryModal") {
    if (selectedVariant === "visitorSummary") {
      return (
        <Box
          style={{
            display: "flex",
            flex: 1,
            gap: 16,
            flexDirection: "column",
            padding: "8px 8px 8px 0",
          }}
        >
          <Typography className={classes.secondTitle}>
            {t("campaign.notifications.settings.pairing.collectData")}
          </Typography>
          <Typography sx={{ fontSize: 14, color: theme.palette.natural.t2 }}>
            {t("campaign.notifications.settings.pairing.cantEdit")}
          </Typography>
          <CustomTextField
            style={{ maxWidth: 780 }}
            disabled
            label={
              t("campaign.notifications.settings.pairing.pairingCondition") ??
              ""
            }
            value={t("campaign.notifications.settings.pairing.domainVisitors")}
          />
          <CustomTextField
            style={{ maxWidth: 780 }}
            disabled
            label={
              t("campaign.notifications.settings.pairing.selectedDomain") ?? ""
            }
            value={activeWebUrl || ""}
          />
          <Typography sx={{ fontSize: 14, color: theme.palette.natural.t2 }}>
            {t("campaign.notifications.settings.pairing.campaignCollects") ??
              ""}
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box className={classes.secondTabContainer}>
          <Typography className={classes.secondTitle}>
            {t("campaign.notifications.settings.pairing.collectData")}
          </Typography>
          {renderForms()}
        </Box>
      );
    }
  }

  if (type === "actualVisitors") {
    return (
      <Box
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          padding: "16px 0",
        }}
      >
        <Typography className={classes.secondTitle}>
          {t("campaign.notifications.settings.pairing.collectData")}
        </Typography>
        <Typography sx={{ fontSize: 14, color: theme.palette.natural.t2 }}>
          {t("campaign.notifications.settings.pairing.cantEdit")}
        </Typography>
        <CustomTextField
          style={{ maxWidth: 780, marginTop: 40 }}
          disabled
          label={
            t("campaign.notifications.settings.pairing.pairingCondition") ?? ""
          }
          value={t("campaign.notifications.settings.pairing.pageVisitors")}
        />
        <CustomTextField
          style={{ maxWidth: 780, marginTop: 40 }}
          disabled
          label={
            t("campaign.notifications.settings.pairing.selectedDomain") ?? ""
          }
          value={activeWebUrl || ""}
        />
      </Box>
    );
  }

  return null;
};

const useStyles = makeStyles((theme: Theme) => ({
  secondTitle: {
    color: theme.palette.main.secondary.u1,
    fontSize: 18,
    fontWeight: "bold",
  },
  formContainer: {
    flex: 1,
    padding: 24,
    backgroundColor: theme.palette.natural.t8,
    marginBottom: 16,
    borderRadius: 8,
    maxWidth: 735,
  },
  urlContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: theme.palette.main.secondary.u1,
  },
  row: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  icon: {
    color: theme.palette.main.alert.o,
    fontSize: 24,
    cursor: "pointer",
  },
  secondTabContainer: {
    display: "flex",
    flex: 1,
    padding: "8px 0",
    flexDirection: "column",
    height: 522,
    overflow: "auto",
  },
}));

export default NotificationPairingTab;
